import React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Modal from '@mui/material/Modal';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import FormGroup from '@mui/material/FormGroup';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import IconButton from '@mui/material/IconButton';
import ListSubheader from '@mui/material/ListSubheader';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  useSelector,
  useDispatch,
} from 'react-redux';

import {
  addHorseAccess,
  updateHorseAccess,
} from '../../../../../../../redux/horseAccess/actions';
import HorseAccessTypeSelect from '../../../../../../../components/EnumSelects/HorseAccessTypeSelect';
import NewUserModal from '../NewUserModal';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 400,
  bgcolor: 'background.paper',
  p: 4,
  overflow: 'auto',
  maxHeight: '95%',
};

const defaultConfig = {
  canAddHorseNotes: false,
  canModifyHorseFeed: false,
  canModifyHorseLocation: false,
  canModifyHorseProfile: false,
  canModifyHorseProfilePicture: false,
  canViewHorseBillables: false,
}

const AddEditModal = ({
  open,
  onClose,
  horseId,
  horseAccess,
}) => {
  // eslint-disable-next-line no-unused-vars
  const dispatch = useDispatch();

  // React state
  const [barnUserId, setBarnUserId] = React.useState('');
  const [barnUserValidation, setBarnUserValidation] = React.useState(false);
  const [type, setType] = React.useState('Client');
  const [accessNote, setAccessNote] = React.useState('');
  const [config, setConfig] = React.useState(defaultConfig);
  const [owner, setOwner] = React.useState(false);
  const [ownershipPercentage, setOwnershipPercentage] = React.useState(0);
  const [ownershipNotes, setOwnershipNotes] = React.useState('');
  const [savingError, setSavingError] = React.useState(null);
  const [addNewUser, setAddNewUser] = React.useState(false);

  // Redux state
  const { isFetchingHorseAccess } = useSelector((state) => state.horse);
  const barnUsers = useSelector((state) => state.barn.barnUsers);
  const isLoading = isFetchingHorseAccess;

  React.useEffect(() => {
    setBarnUserId(horseAccess
      ? horseAccess.user.id
      : '');
    setType(horseAccess
      ? horseAccess.type
      : 'Client');
    setAccessNote(horseAccess
      ? horseAccess.accessNote
      : '');
    setOwner(horseAccess
      ? horseAccess.owner
      : false);
    setConfig(horseAccess
      ? horseAccess.config || defaultConfig
      : defaultConfig);
    setOwnershipPercentage(horseAccess
      ? horseAccess.ownershipPercentage
      : 0);
    setOwnershipNotes(horseAccess
      ? horseAccess.ownershipNotes
      : '');
  }, [horseAccess, open]);

  const handleSave = () => {
    const isNewHorseAccess = !horseAccess || !horseAccess.id;
    if (!barnUserId) {
      setBarnUserValidation(true);
      return;
    }

    const request = {
      accessNote,
      startDate: (horseAccess && horseAccess.id)
        ? null
        : new Date(),
      config,
      owner,
      ownershipNotes,
      ownershipPercentage,
      type,
    };

    // Adding new horse access
    if (isNewHorseAccess) {
      dispatch(addHorseAccess(horseId, barnUserId, request)).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to give this user access to the horse. Please contact Questri support.');
        console.error(error);
      });
    } else {
      dispatch(updateHorseAccess(horseId, barnUserId, request)).then(() => {
        onClose();
      }).catch((error) => {
        setSavingError(error.message || 'There was an error attempting to update this user access. Please contact Questri support.');
        console.error(error);
      });
    }
  };

  const handleEndHorseAccess = () => {
    dispatch(updateHorseAccess(horseId, barnUserId, { endDate: new Date() })).then(() => {
      onClose();
    }).catch((error) => {
      setSavingError(error.message || 'There was an error attempting end access for this horse. Please contact Questri support.');
      console.error(error);
    });
  };

  const handleCreateNewUserClick = (e) => {
    e.stopPropagation();
    setAddNewUser(true);
  };

  const handleNewUser = (newUser) => {
    setBarnUserId(newUser.id);
    setAddNewUser(false);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-add-access-note"
      aria-describedby="modal-add-horse-access-description"
    >
      <Box sx={modalStyle}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Typography
              id="modal-add-horse-access-title"
              variant="h6"
              component="h2"
              align="center"
              gutterBottom
            >
              {(horseAccess && horseAccess.id)
                ? 'Edit'
                : 'Add'} Horse Access {horseAccess && `(${horseAccess.user.userFullName})`}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <HorseAccessTypeSelect value={type} onChange={(e) => { setType(e.target.value); }} />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth margin="none">
              <InputLabel id="user-select-label">User</InputLabel>
              <Select
                labelId="user-select-label"
                id="user-select"
                displayEmpty
                value={barnUserId}
                error={barnUserValidation}
                label="User"
                onChange={(e) => setBarnUserId(e.target.value)}
              >
                <ListSubheader>Existing Users</ListSubheader>
                { barnUsers.sort((a, b) => -a.name > b.name)
                  .sort((a, b) => -b.userFullName.localeCompare(a.userFullName)).map((bu) => (
                    <MenuItem key={`barn-user-item-${bu.id}`} value={bu.id}>{`${bu.userFullName} (${bu.email || 'No Email/Account'})`}</MenuItem>
                  ))}
                <ListSubheader>User does not exist yet?</ListSubheader>
                <MenuItem onClick={handleCreateNewUserClick}>
                  <IconButton><AddIcon /></IconButton>
                  <ListItemText primary="Create new user" />
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              value={accessNote}
              onChange={(e) => { setAccessNote(e.target.value); }}
              fullWidth
              disabled={isLoading}
              label="Access Note"
              multiline
              rows={3}
              helperText="Any additional information about this access to provide context."
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl>
              <FormLabel id="permissions-controlled-radio-buttons-group">Permissions</FormLabel>
              <FormGroup>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canAddHorseNotes}
                      onChange={(e) => setConfig({
                        ...config,
                        canAddHorseNotes: e.target.checked,
                      })}
                      name="canAddHorseNotes"
                    />
                  )}
                  label="Can Add Horse Notes"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canModifyHorseFeed}
                      onChange={(e) => setConfig({
                        ...config,
                        canModifyHorseFeed: e.target.checked,
                      })}
                      name="canModifyHorseFeed"
                    />
                  )}
                  label="Can Change Horse Feed"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canModifyHorseLocation}
                      onChange={(e) => setConfig({
                        ...config,
                        canModifyHorseLocation: e.target.checked,
                      })}
                      name="canModifyHorseLocation"
                    />
                  )}
                  label="Can Change Horse Location"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canModifyHorseProfile}
                      onChange={(e) => setConfig({
                        ...config,
                        canModifyHorseProfile: e.target.checked,
                      })}
                      name="canModifyHorseProfile"
                    />
                  )}
                  label="Can Change Horse Profile Data"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canModifyHorseProfilePicture}
                      onChange={(e) => setConfig({
                        ...config,
                        canModifyHorseProfilePicture: e.target.checked,
                      })}
                      name="canModifyHorseProfilePicture"
                    />
                  )}
                  label="Can Change Horse Picture"
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={config.canViewHorseBillables}
                      onChange={(e) => setConfig({
                        ...config,
                        canViewHorseBillables: e.target.checked,
                      })}
                      name="canViewHorseBillables"
                    />
                  )}
                  label="Can View Horse Billables"
                />
              </FormGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormGroup>
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={owner}
                    onChange={(e) => {
                      setOwner(e.target.checked);
                      if (!e.target.checked) {
                        setOwnershipPercentage(0);
                        setOwnershipNotes('');
                      }
                      setOwnershipPercentage(100);
                    }}
                    disabled={isLoading}
                  />
                )}
                label="Is this the owner of the horse."
              />
            </FormGroup>
            {
            owner && (
              <TextField
                value={ownershipPercentage}
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                onChange={(e) => { setOwnershipPercentage(e.target.value); }}
                fullWidth
                disabled={isLoading}
                label="Ownership Percentage"
              />
            )
          }
            {
            owner && (
              <TextField
                sx={{ mt: 2 }}
                value={ownershipNotes}
                onChange={(e) => { setOwnershipNotes(e.target.value); }}
                fullWidth
                disabled={isLoading}
                label="Ownership Note"
                multiline
                rows={3}
                helperText="Any additional information about the ownership of this horse."
              />
            )
          }
          </Grid>
        </Grid>

        <Divider
          sx={{
            mb: '1rem',
            mt: '1rem',
          }}
        />
        <Box sx={{ textAlign: 'right' }}>
          { savingError && <Typography variant="caption" component="div" color="error" gutterBottom>{savingError}</Typography>}

          {horseAccess && <Button sx={{ float: 'left' }} onClick={handleEndHorseAccess} disabled={isLoading}>End Horse Access</Button>}
          <Button onClick={onClose} disabled={isLoading}>Cancel</Button>
          <LoadingButton onClick={handleSave} disabled={isLoading} variant="contained" color="primary" loading={isLoading} loadingPosition="start" startIcon={<SaveIcon />}>Save</LoadingButton>
        </Box>

        <NewUserModal open={addNewUser} onCancel={() => setAddNewUser(false)} onNewUser={handleNewUser} />
      </Box>
    </Modal>
  );
};

AddEditModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  horseId: PropTypes.string.isRequired,
  horseAccess: PropTypes.object,
};

AddEditModal.defaultProps = { horseAccess: null };

export default AddEditModal;
